import {observer, Popover2, React, useEffect, useLocalStore} from '@api'
import {Tooltip2} from '@api'
import {GroupAssortmentInternallyOrExternallyToggleButton, GroupByDropdownContentPanel, GroupByDropdownStore, GroupByDropdownStoreContext, GroupByToolbarChip} from '@modules/catalog'
import {guid} from '@utils'
import {action, autorun, computed, observable, runInAction} from 'mobx'
import {useContext} from 'react'
import * as css from './GroupByToolbarChips.css'
import {GroupByGrouping, site, StoresInstance, ui} from '@stores'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {LabeledButton, MyPopover2, PrimaryIconButton} from 'centric-ui-components'
import * as dropdownCss from './GroupByDropdown.css'

/**
 * A list of dropdown buttons that represent each level of the hierarchy.  Can be dragged and dropped to reordered, cleared, etc.
 *
 * Used in the top toolbar
 */
export const GroupByToolbarChips = observer(function GroupByToolbarChips() {
  const store = useVipLocalStore((ws) => new GroupByToolbarChipsStore(ws)),
    {setRef, strings, catalog} = store,
    {isProductView, assortments: {selectedAssortment}, groupBy: {hierarchyGroups, maxGroupDepth, clearHierarchy, increaseDepth, catalogItemsGroup, settings: {depth}}} = catalog

  useEffect(() => store.registerEffects(), [])

  // if (showGroupByOnFilterBar) return <GroupByToolbarChips_old />

  let groupsToRender = [...hierarchyGroups]
  if (groupsToRender.length < maxGroupDepth && _.last(groupsToRender) !== catalogItemsGroup) {
    groupsToRender.push(catalogItemsGroup)
  }

  const {onPopoverInteraction, onPopoverOpening, onPopoverClosing, initialSize, selectedDropdownStore, open} = store

  return (
    <GroupByToolbarChipsStoreContext.Provider value={store}>
      <GroupByDropdownStoreContext.Provider value={selectedDropdownStore}>
      <Popover2
        //interactionKind='hover'
        position='bottom-left'
        // modifiers={{
        //   offset: {
        //     enabled: true,
        //     options: {
        //       offset: [0, 22]
        //     }
        //   }
        // }}
        ref={setRef}
        minimal
        isOpen={open}
        onInteraction={onPopoverInteraction}
        onOpened={onPopoverOpening}
        onClosed={onPopoverClosing}
        className={dropdownCss.popoverContainer}
        autoFocus={false}
        portalClassName={dropdownCss.portal}
        content={
          <GroupByDropdownContentPanel style={!initialSize ? undefined : {minWidth: initialSize.width/*, minHeight: initialSize.height*/}} />
      }>
        <div className={css.root} data-type='GroupByToolbarChips' data-open={open}>
          <label>{strings.groupBy}:</label>

          { // If we don't key by index then every time the hierarchy changes we'll lose our open dropdown menu
            groupsToRender.filter(v => v).map((group, index) => <GroupByToolbarChip index={index} group={group} key={index}/>)
          }

          {selectedAssortment && <GroupAssortmentInternallyOrExternallyToggleButton/>}

          {!isProductView &&
           <Tooltip2 content={strings.clearGroups} placement='bottom'>
             <PrimaryIconButton showOnHover icon='clean' onClick={clearHierarchy}/>
           </Tooltip2>}
        </div>
      </Popover2>
      </GroupByDropdownStoreContext.Provider>
    </GroupByToolbarChipsStoreContext.Provider>)
})

export const GroupByToolbarChipsStoreContext = React.createContext<GroupByToolbarChipsStore>(undefined)
export const useGroupByToolbarChipsStore = () => useContext(GroupByToolbarChipsStoreContext)

/**
 * Items in the hierarchy (groups) are draggable to reorder, can be deleted, have dropdowns to allow changing the selected group, etc)
 */
export class GroupByToolbarChipsStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
    this.groupBy.chipsStore = this
  }

  @observable popoverKey = guid()

  @observable.ref popover : Popover2<any>
  @action setRef = p => this.popover = p

  registerEffects = () => {
    const {stores, groupBy} = this
    const toDispose = [
      autorun(() => {
        if (this.isOpen && site.activePopover && site.activePopover != this.popover) {
          this.isOpen = false
          this.selectedIndex = null
        }
      }),

      autorun(() => {
        const {hierarchy} = groupBy
        if (hierarchy.length) {
          runInAction(() => {
            const {selectedIndex, isOpen} = this
            if (isOpen) {
              if (hierarchy.length < groupBy.depth) {
                this.selectedIndex = -1;
              } else {
                this.selectedIndex = hierarchy.length - 1
              }
            }
          })
        }
      }),
      autorun(() => {
        const {selectedGroup} = this,
          group = selectedGroup ?? groupBy.catalogItemsGroup

        if (group) {
          const r = new GroupByDropdownStore(stores, {group: group, level: group.level}, 'toolbar')
          r.popoverHasOpened = true

          this.groupByDropdownStores.set(group.level, r)
        }

        if (group === groupBy.catalogItemsGroup && groupBy.menuStore) {
          groupBy.menuStore.subtitleButtonActive = false
        }
      })
    ]

    return () => toDispose.forEach(f => f())
  }
  groupByDropdownStores = observable.map<number, GroupByDropdownStore>({}, {deep: false})

  @observable dragItem: GroupByGrouping = null
  @observable dragOverItem: GroupByGrouping = null

  @action clearDragState = () => {
    this.dragItem = null
    this.dragOverItem = null
  }

  @observable selectedSubtitle: string
  @observable selectedIndex = null

  @computed get selectedGroup() {
    const {selectedIndex, groupBy} = this
    return selectedIndex === null ? null : selectedIndex == -1 ? groupBy.catalogItemsGroup : groupBy.hierarchyGroups[selectedIndex]
  }

  @computed get selectedDropdownStore() : GroupByDropdownStore {
    const {selectedGroup, groupByDropdownStores, groupBy} = this
    return !selectedGroup ? groupByDropdownStores.get(groupBy.isProductView ? -1 : 0) : groupByDropdownStores.get(selectedGroup.level)
  }

  @action onPopoverInteraction = (nextOpenState: boolean, e?: React.SyntheticEvent<HTMLElement>) => {
    // console.log(`onPopoverInteraction`, {nextOpenState})
    if (!nextOpenState) {
      if (!e || !(e.target as HTMLElement)?.closest('[data-type="GroupByToolbarChip"]')) {
        this.selectedIndex = null
        this.selectedSubtitle = null
      }
    }
  }

  @observable.ref initialSize : {width, height}

  @observable isOpen = false

  @action onPopoverOpening = (node: HTMLElement) => {
    this.catalog.hoverPopoverItemId = null
    this.isOpen = true
    node && setTimeout(() => {
      this.initialSize = _.pick(node.getBoundingClientRect(), ['width', 'height'])
    }, 200) // Expensive
  }

  @action onPopoverClosing = () => {
    console.log(`onPopoverClosing`)
    // ignore these as popover closed is no longer a valid thing with MyPopover2
    // this.isOpen = false
    // this.initialSize = null
    //setTimeout(() => this.groupBy.menuStore.searchText = '', 50)  Why??
  }

  @computed get open() {
    return this.selectedGroup != null
  }
}



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Menus/Toolbar/GroupChips/GroupByToolbarChips.tsx